import { useMemo, useState } from 'react';
import type { TaskResponse } from '@orion/contracts';
import { Button, EmptyState, Menu } from '@orion/ui';
import { PageHeader } from '../../app/shell.js';
import { NotesPanel } from '../notes/NotesPanel.js';
import { useCreateTask, useTaskCountsQuery, useTaskQuery, useTasksQuery, useUpdateTask } from './api.js';

type View = 'mine' | 'team' | 'overdue' | 'upcoming' | 'completed';
type Kind = TaskResponse['kind'];

const VIEWS: Array<{ key: View; label: string }> = [
  { key: 'mine', label: 'My tasks' },
  { key: 'team', label: 'Team tasks' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'completed', label: 'Completed' },
];

const KIND_ICON: Record<Kind, { label: string; path: string; isRect?: boolean }> = {
  todo: { label: 'To-do', path: 'm8 12 3 3 5-6', isRect: true },
  call: { label: 'Call', path: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2' },
  email: { label: 'Email', path: 'm3 7 9 6 9-6', isRect: true },
  meeting: { label: 'Meeting', path: 'M16 3v4M8 3v4M3 10h18', isRect: true },
};

const PRIORITY_STYLE: Record<TaskResponse['priority'], { color: string; label: string }> = {
  high: { color: '#B42318', label: 'High' },
  normal: { color: '#C7781A', label: 'Normal' },
  low: { color: '#9A9FA8', label: 'Low' },
};

function KindIcon({ kind, stroke = '#5E6470' }: { kind: Kind; stroke?: string }) {
  const def = KIND_ICON[kind];
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-label={def.label} role="img">
      {kind === 'todo' && <rect x="3" y="3" width="18" height="18" rx="3" />}
      {kind === 'email' && <rect x="3" y="5" width="18" height="14" rx="2" />}
      {kind === 'meeting' && <rect x="3" y="5" width="18" height="16" rx="2" />}
      <path d={def.path} />
    </svg>
  );
}

function formatDueLabel(iso: string): { text: string; overdue: boolean } {
  const date = new Date(iso);
  const now = new Date();
  const overdue = date < now;
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) return { text: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }), overdue };
  return { text: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), overdue };
}

/** "Overdue" means an incomplete task whose due day has already passed —
 * not merely an earlier hour today, and never a completed task (a task
 * finished this morning still belongs in "Today", not "Overdue"). */
function groupOf(t: TaskResponse): 'overdue' | 'today' | 'tomorrow' | 'week' | 'later' | 'none' {
  if (!t.dueAt) return 'none';
  const date = new Date(t.dueAt);
  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTomorrow = new Date(startOfToday.getTime() + 86_400_000);
  const startOfDayAfter = new Date(startOfTomorrow.getTime() + 86_400_000);
  const endOfWeek = new Date(startOfToday.getTime() + 7 * 86_400_000);
  if (t.status !== 'completed' && date < startOfToday) return 'overdue';
  if (date < startOfTomorrow) return 'today';
  if (date < startOfDayAfter) return 'tomorrow';
  if (date < endOfWeek) return 'week';
  return 'later';
}

const GROUP_LABEL: Record<string, string> = {
  overdue: 'Overdue',
  today: 'Today',
  tomorrow: 'Tomorrow',
  week: 'Later this week',
  later: 'Later',
  none: 'No due date',
};
const GROUP_ORDER = ['overdue', 'today', 'tomorrow', 'week', 'later', 'none'];

/** docs section 15 "Task Management" — My/Team/Overdue/Upcoming/Completed
 * views grouped by due date, with a detail side panel. Matches design/
 * orion-crm-design/rendered-html/Tasks.html's grid columns and grouping.
 *
 * A task can relate to at most one record (packages/contracts's
 * `relatedRecordRefinement`), so the detail panel shows a single "Related
 * to" chip, not the two the design's example row happens to show. There's
 * no "Call now" dialer action — that needs a telephony integration this
 * pass doesn't add. */
export function TasksPage() {
  const [view, setView] = useState<View>('mine');
  const [q, setQ] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<TaskResponse['priority'] | undefined>(undefined);
  const [kindFilter, setKindFilter] = useState<Kind | undefined>(undefined);
  // The design screenshot captures the panel open on one row — auto-select
  // it under `?__design=Tasks` the same way LeadDetailPage auto-opens
  // ConvertLeadModal under `?__design=LeadConvert` (dev-only).
  const [selectedId, setSelectedId] = useState<string | undefined>(() =>
    import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design') === 'Tasks' ? 't6' : undefined,
  );
  const [newTitle, setNewTitle] = useState('');

  const { data: counts } = useTaskCountsQuery();
  const { data, isLoading } = useTasksQuery({ view, q: q || undefined, pageSize: 100 });
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();

  const tasks = useMemo(() => {
    let rows = data?.data ?? [];
    if (priorityFilter) rows = rows.filter((t) => t.priority === priorityFilter);
    if (kindFilter) rows = rows.filter((t) => t.kind === kindFilter);
    return rows;
  }, [data, priorityFilter, kindFilter]);

  const groups = useMemo(() => {
    const buckets = new Map<string, TaskResponse[]>();
    for (const t of tasks) {
      const key = groupOf(t);
      const bucket = buckets.get(key) ?? [];
      bucket.push(t);
      buckets.set(key, bucket);
    }
    return GROUP_ORDER.filter((k) => buckets.has(k)).map((key) => ({ key, label: GROUP_LABEL[key]!, items: buckets.get(key)! }));
  }, [tasks]);

  const addTask = async () => {
    if (!newTitle.trim()) return;
    await createTask.mutateAsync({ title: newTitle.trim(), kind: 'todo', priority: 'normal' });
    setNewTitle('');
  };

  const complete = (t: TaskResponse) => updateTask.mutateAsync({ id: t.id, input: { status: t.status === 'completed' ? 'open' : 'completed', version: t.version } });

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'Tasks' }]} />
      <main className="grid flex-1 grid-cols-[1fr_340px] gap-4 overflow-hidden px-7 pb-6 pt-5">
        <div className="flex min-h-0 flex-col gap-3">
          <div className="flex h-10 shrink-0 items-center justify-between">
            <div className="flex items-baseline gap-3">
              <h1 className="text-[22px] font-[650] leading-none tracking-[-0.01em] text-ink">Tasks</h1>
              {counts && (
                <span className="text-sm text-text-muted">
                  {counts.dueToday} due today
                  {counts.overdue > 0 && (
                    <>
                      {' · '}
                      <span className="font-semibold text-danger">{counts.overdue} overdue</span>
                    </>
                  )}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addTask()}
                placeholder="Quick add a task…"
                className="h-9 w-56 rounded-control border border-border-strong bg-surface px-3 text-sm"
              />
              <Button size="sm" onClick={addTask} loading={createTask.isPending}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                New task
              </Button>
            </div>
          </div>

          <div role="tablist" aria-label="Task views" className="flex h-10 shrink-0 items-stretch gap-5.5 border-b border-border">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                type="button"
                role="tab"
                aria-selected={view === v.key}
                onClick={() => setView(v.key)}
                className={view === v.key ? 'flex items-center gap-1.5 text-sm font-semibold text-ink shadow-[inset_0_-2px_0_var(--color-primary)]' : 'flex items-center gap-1.5 text-sm font-medium text-text-muted hover:text-ink'}
              >
                {v.label}
                {v.key === 'mine' && counts && counts.mine > 0 && <span className="rounded-full bg-primary-soft px-1.75 py-px text-xs font-semibold text-primary-soft-text">{counts.mine}</span>}
                {v.key === 'overdue' && counts && counts.overdue > 0 && <span className="rounded-full bg-danger-soft px-1.75 py-px text-xs font-semibold text-danger">{counts.overdue}</span>}
              </button>
            ))}
          </div>

          <div className="flex h-8 shrink-0 items-center gap-2">
            <label className="flex h-8 w-60 items-center gap-2 rounded-control border border-border-strong bg-surface px-2.5">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <span className="sr-only">Search tasks</span>
              <input value={q} onChange={(e) => setQ(e.target.value)} type="search" placeholder="Search tasks…" className="w-full bg-transparent text-sm text-ink placeholder:text-text-muted focus:outline-none" />
            </label>
            <Menu
              trigger={
                <button type="button" className="flex h-8 items-center gap-1 rounded-chip border border-dashed border-border-strong bg-surface px-2.5 text-[13px] font-medium text-text-secondary">
                  Priority{priorityFilter ? `: ${PRIORITY_STYLE[priorityFilter].label}` : ''}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>
              }
              items={[
                { label: 'Any priority', onSelect: () => setPriorityFilter(undefined) },
                { label: 'High', onSelect: () => setPriorityFilter('high') },
                { label: 'Normal', onSelect: () => setPriorityFilter('normal') },
                { label: 'Low', onSelect: () => setPriorityFilter('low') },
              ]}
              align="left"
            />
            <Menu
              trigger={
                <button type="button" className="flex h-8 items-center gap-1 rounded-chip border border-dashed border-border-strong bg-surface px-2.5 text-[13px] font-medium text-text-secondary">
                  Type{kindFilter ? `: ${KIND_ICON[kindFilter].label}` : ''}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>
              }
              items={[
                { label: 'Any type', onSelect: () => setKindFilter(undefined) },
                ...(Object.keys(KIND_ICON) as Kind[]).map((k) => ({ label: KIND_ICON[k].label, onSelect: () => setKindFilter(k) })),
              ]}
              align="left"
            />
          </div>

          {!isLoading && tasks.length === 0 && <EmptyState title="Nothing here" description="You're all caught up." />}

          <div className="min-h-0 flex-1 overflow-y-auto rounded-card border border-border bg-surface shadow-card">
            {groups.map((group) => (
              <div key={group.key}>
                <div
                  className="flex h-7.5 items-center gap-2 border-b px-4"
                  style={
                    group.key === 'overdue'
                      ? { background: 'var(--color-danger-soft)', borderColor: '#F6D5D1' }
                      : { background: 'var(--color-surface-subtle)', borderColor: 'var(--color-border)' }
                  }
                >
                  <span className={`text-[11px] font-semibold uppercase tracking-[0.08em] ${group.key === 'overdue' ? 'text-danger' : 'text-ink'}`}>{group.label}</span>
                  <span className={`text-xs font-semibold ${group.key === 'overdue' ? 'text-danger' : 'text-text-muted'}`}>{group.items.length}</span>
                </div>
                {group.items.map((t) => {
                  const due = t.dueAt ? formatDueLabel(t.dueAt) : null;
                  const done = t.status === 'completed';
                  return (
                    <div
                      key={t.id}
                      onClick={() => setSelectedId(t.id)}
                      className={`grid h-10 cursor-pointer items-center gap-2 border-b border-divider px-4 text-[13px] last:border-0 ${selectedId === t.id ? 'bg-primary-soft/40' : 'hover:bg-surface-subtle'}`}
                      style={{ gridTemplateColumns: '24px 22px minmax(0,1fr) 196px 64px 84px 18px 24px' }}
                    >
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={done}
                        aria-label={`${done ? 'Completed' : 'Complete'}: ${t.title}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          complete(t);
                        }}
                        className={`size-4.5 rounded-full border-[1.5px] ${done ? 'flex items-center justify-center border-success bg-success' : 'border-border-strong bg-surface'}`}
                      >
                        {done && (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="m5 12 5 5 9-10" />
                          </svg>
                        )}
                      </button>
                      <KindIcon kind={t.kind} stroke={done ? '#9A9FA8' : '#5E6470'} />
                      <span className={`truncate font-medium ${done ? 'text-text-muted line-through' : 'text-ink'}`}>{t.title}</span>
                      {t.relatedTo ? (
                        <span className="flex h-6 items-center gap-1.5 justify-self-start truncate rounded-[6px] border border-border bg-surface-subtle px-2 text-xs text-text-secondary">
                          {(t.relatedTo.type === 'lead' || t.relatedTo.type === 'contact') && `${t.relatedTo.type === 'lead' ? 'Lead' : 'Contact'} · `}
                          {t.relatedTo.label}
                        </span>
                      ) : (
                        <span />
                      )}
                      <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: PRIORITY_STYLE[t.priority].color }}>
                        {PRIORITY_STYLE[t.priority].label}
                      </span>
                      {done ? (
                        <span className="text-xs font-semibold text-success">Done{t.completedAt ? ` ${new Date(t.completedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}</span>
                      ) : (
                        <span className={`text-xs ${due?.overdue ? 'font-semibold text-danger' : 'text-text-secondary'}`}>{due?.text ?? '—'}</span>
                      )}
                      {t.remindAt ? (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-label="Reminder set" role="img">
                          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                          <path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" />
                        </svg>
                      ) : (
                        <span />
                      )}
                      {t.owner ? (
                        <span className="flex size-5.5 items-center justify-center rounded-full bg-night text-[9px] font-[650] text-white">
                          {t.owner.displayName
                            .split(' ')
                            .map((p) => p[0])
                            .join('')
                            .slice(0, 2)
                            .toUpperCase()}
                        </span>
                      ) : (
                        <span />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <TaskDetailPanel taskId={selectedId} onClose={() => setSelectedId(undefined)} />
      </main>
    </>
  );
}

function TaskDetailPanel({ taskId, onClose }: { taskId: string | undefined; onClose: () => void }) {
  const { data: task } = useTaskQuery(taskId);
  const updateTask = useUpdateTask();
  const [description, setDescription] = useState('');

  if (!taskId || !task) {
    return (
      <aside aria-label="Task detail" className="flex min-h-0 flex-col items-center justify-center rounded-card border border-border bg-surface p-6 text-center text-sm text-text-muted shadow-popover">
        Select a task to see its details.
      </aside>
    );
  }

  const reminderMinutesBefore = task.remindAt && task.dueAt ? Math.round((new Date(task.dueAt).getTime() - new Date(task.remindAt).getTime()) / 60_000) : null;

  return (
    <aside aria-label="Task detail" className="flex min-h-0 flex-col overflow-hidden rounded-card border border-border bg-surface shadow-popover">
      <div className="flex flex-col gap-2 border-b border-border p-4">
        <div className="flex items-center gap-2">
          <span className="flex h-5.5 items-center gap-1.5 rounded-[6px] bg-teal-soft px-2 text-xs font-semibold" style={{ color: 'var(--color-teal)' }}>
            <KindIcon kind={task.kind} stroke="var(--color-teal)" />
            {KIND_ICON[task.kind].label}
          </span>
          <div className="flex-1" />
          <button type="button" aria-label="Close panel" onClick={onClose} className="flex size-7 items-center justify-center rounded-chip hover:bg-surface-subtle">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <h2 className="text-[17px] font-[650] leading-5.5 text-ink">{task.title}</h2>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
          Status
          <select
            value={task.status}
            onChange={(e) => updateTask.mutate({ id: task.id, input: { status: e.target.value as typeof task.status, version: task.version } })}
            className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-sm font-normal"
          >
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </label>

        <dl className="grid grid-cols-[84px_1fr] items-center gap-x-2 gap-y-2.5 text-[13px] leading-4.5">
          <dt className="text-text-muted">Due</dt>
          <dd className="font-semibold">{task.dueAt ? new Date(task.dueAt).toLocaleString('en-US', { weekday: undefined, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}</dd>
          <dt className="text-text-muted">Reminder</dt>
          <dd className="flex items-center gap-1.5">
            {task.remindAt ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                  <path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" />
                </svg>
                {reminderMinutesBefore !== null && reminderMinutesBefore >= 0 ? `${reminderMinutesBefore} min before` : new Date(task.remindAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </>
            ) : (
              '—'
            )}
          </dd>
          <dt className="text-text-muted">Priority</dt>
          <dd className="flex items-center gap-1.5 font-semibold" style={{ color: PRIORITY_STYLE[task.priority].color }}>
            {PRIORITY_STYLE[task.priority].label}
          </dd>
          <dt className="text-text-muted">Assigned to</dt>
          <dd className="flex items-center gap-1.5">
            {task.owner ? (
              <>
                <span className="flex size-5 items-center justify-center rounded-full bg-night text-[9px] font-[650] text-white">
                  {task.owner.displayName
                    .split(' ')
                    .map((p) => p[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
                {task.owner.displayName}
              </>
            ) : (
              'Unassigned'
            )}
          </dd>
          {task.relatedTo && (
            <>
              <dt className="self-start pt-0.5 text-text-muted">Related to</dt>
              <dd>
                <span className="inline-flex h-6 items-center gap-1.5 rounded-[6px] border border-border bg-surface-subtle px-2 text-xs text-ink">{task.relatedTo.label}</span>
              </dd>
            </>
          )}
        </dl>

        <div className="h-px bg-divider" />

        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
          Description
          <textarea
            defaultValue={task.description ?? ''}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => description && description !== task.description && updateTask.mutate({ id: task.id, input: { description, version: task.version } })}
            rows={3}
            className="resize-none rounded-control border border-border-strong bg-surface px-3 py-2 text-[13px] font-normal text-ink"
          />
        </label>

        <NotesPanel taskId={task.id} />
      </div>

      <div className="flex gap-2 border-t border-border bg-surface-subtle p-3">
        <Button
          className="flex-1 justify-center"
          onClick={() => updateTask.mutate({ id: task.id, input: { status: task.status === 'completed' ? 'open' : 'completed', version: task.version } })}
          loading={updateTask.isPending}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m5 12 5 5 9-10" />
          </svg>
          {task.status === 'completed' ? 'Reopen task' : 'Complete task'}
        </Button>
      </div>
    </aside>
  );
}
